import { getLogger } from '@confluence/logger';
import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';

type PromptCallback = (args: {
	previousMatch: RouteMatch | null;
	nextMatch: RouteMatch;
}) => boolean;

/**
 * GlobalPromptHandler is responsible for managing a single prompt string and its associated callback, replacing react-router's Prompt functionality.
 * This class allows registration of a prompt message with a callback that determines whether
 * the prompt should be shown, and provides functionality to clear the stored state.
 */
export class GlobalPromptHandler {
	private logger = getLogger('GlobalPromptHandler');
	private promptString: string | null = null;
	private promptCallback: PromptCallback | null = null;
	private onConfirm: (() => void) | null = null;
	private onCancel: (() => void) | null = null;

	/**
	 * Registers a prompt string with its associated callback function.
	 * This should be used in place of react-router's Prompt functionality. When registered, the callback
	 * will be executed on every route change attempt, blocking the route change (the transition).
	 *
	 * The callback should return:
	 * - true to show the prompt and block navigation
	 * - false to allow navigation without prompting
	 *
	 * User interaction flow:
	 * - If callback returns true, the user will see a browser confirmation dialog (window.confirm) with the provided message.
	 * - If user clicks "OK", the route change continues and the prompt is automatically cleared.
	 * - If user clicks "Cancel", the route change is prevented and the prompt remains active.
	 * - If the route change is successful even if prompt wasn't shown, the prompt state is cleared automatically.
	 *
	 * Technical Reminders:
	 * - When registering a prompt, ensure your implementation clears the prompt if it is no longer needed, by invoking clear().
	 * - There can only be a maximum of one prompt registered at a time. Ensure your code flow won't conflict with existing use cases of registerPrompt.
	 *
	 * @param {Object} params - The parameters for the prompt registration.
	 * @param {string} params.promptString - The message displayed in the browser confirmation dialog.
	 * @param {PromptCallback} params.callback - A function that receives route transition info and returns true to show the confirmation dialog, false to allow navigation without prompting.
	 */
	registerPrompt({
		promptString,
		callback,
		onConfirm,
		onCancel,
	}: {
		promptString: string;
		callback: PromptCallback;
		onConfirm?: () => void;
		onCancel?: () => void;
	}): void {
		this.promptString = promptString;
		this.promptCallback = callback;
		this.onConfirm = onConfirm ?? null;
		this.onCancel = onCancel ?? null;
	}

	/**
	 * Clears the currently stored prompt string and callback, resetting the handler state.
	 * This is done automatically on successful route change, but you should invoke it if you registered the prompt and no longer need it.
	 */
	clear(): void {
		this.promptString = null;
		this.promptCallback = null;
		this.onConfirm = null;
		this.onCancel = null;
	}

	/**
	 * Gets the current prompt string if it exists and the callback returns true.
	 *
	 * @param {Object} args - The route transition arguments.
	 * @param {RouteMatch | null} args.previousMatch - The previous route match.
	 * @param {RouteMatch} args.nextMatch - The next route match.
	 * @returns {string | null} The prompt string if conditions are met, null otherwise.
	 */
	getPromptString(args: {
		previousMatch: RouteMatch | null;
		nextMatch: RouteMatch;
	}): string | null {
		if (!this.promptString || !this.promptCallback) {
			return null;
		}

		try {
			const shouldPrompt = this.promptCallback({
				previousMatch: args.previousMatch,
				nextMatch: args.nextMatch,
			});

			return shouldPrompt ? this.promptString : null;
		} catch (error) {
			this.logger.error`Error executing prompt callback: ${error}`;
			return null;
		}
	}

	/**
	 * Triggers the prompt if it exists and the prompt callback returns true.
	 *
	 * @param {Object} args - The route transition arguments.
	 * @param {RouteMatch | null} args.previousMatch - The previous route match.
	 * @param {RouteMatch} args.nextMatch - The next route match.
	 * @returns {Object} Returns {isPromptBlocking: true} if transition should be blocked because prompt was shown and user canceled,
	 *                   otherwise returns {isPromptBlocking: false}
	 */
	triggerPrompt({
		previousMatch,
		nextMatch,
	}: {
		previousMatch: RouteMatch | null;
		nextMatch: RouteMatch;
	}): { isPromptBlocking: boolean } {
		if (!this.promptString) {
			return { isPromptBlocking: false };
		}

		const promptString = this.getPromptString({
			previousMatch,
			nextMatch,
		});

		if (promptString) {
			const userConfirmed = window.confirm(promptString);
			if (userConfirmed) {
				this.onConfirm?.();
				this.clear();
			} else {
				this.onCancel?.();
				// User cancelled, block the transition
				return { isPromptBlocking: true };
			}
		}

		return { isPromptBlocking: false };
	}
}
